import { useCallback } from 'react';
import { omit } from 'lodash-es';
import type { CallIntelTypes } from '@frontend/api-call-intel';
import { AnySearch, useNavigate, useSearch } from '@frontend/weave-router';
import { URLS } from '../utils/constant';
import { getSubViewRoute, getSubViewSearchParams, type ExtendedCallIntelSubView } from '../utils/navigation-config';

export type NavigateToCallIntelArgs = {
  tab?: CallIntelTypes.CallIntelTab;
  filters?: Partial<CallIntelTypes.Filters>;
  subView?: ExtendedCallIntelSubView;
  // For tracking / attribution
  mailer?: string;
  metric?: string;
};

const encodeFilters = (filters: unknown) => {
  const json = JSON.stringify(filters);
  return encodeURIComponent(btoa(json));
};

/**
 * Hook for Call Intelligence navigation.
 */
export const useCallIntelNavigator = () => {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });

  /**
   * Checks if the current search params contain deep link parameters
   */
  const hasDeepLinkParams = useCallback((currentSearch: AnySearch) => {
    return !!(
      currentSearch?.tab ||
      currentSearch?.subViewType ||
      currentSearch?.subViewId ||
      currentSearch?.filters ||
      currentSearch?.mailer ||
      currentSearch?.metric
    );
  }, []);

  /**
   * Validates if subView data exists either in search params or store.
   */
  const hasValidSubView = useCallback(
    (storeSubView?: { id?: string; type?: string }) => {
      return !!((searchParams?.subViewType && searchParams?.subViewId) || (storeSubView?.id && storeSubView?.type));
    },
    [searchParams]
  );

  const navigateToCallIntel = useCallback(
    (args: NavigateToCallIntelArgs = {}) => {
      const { tab, filters, subView, mailer, metric } = args;

      const search: Record<string, any> = {};

      if (tab) search.tab = tab;

      if (mailer) search.mailer = mailer;
      if (metric) search.metric = metric;

      if (filters && Object.keys(filters).length) {
        search.filters = encodeFilters(filters);
      }

      // Determine the route based on subView
      let route = URLS.CALL_INTEL_MAIN_PAGE;

      if (subView?.type && subView?.id) {
        const subViewRoute = getSubViewRoute(subView);
        if (subViewRoute) {
          search.subViewType = subView.type;
          search.subViewId = subView.id;

          route = subViewRoute;
          // Add type to search params if needed (for generic routes)
          const subViewSearchParams = getSubViewSearchParams(subView);
          Object.assign(search, subViewSearchParams);
        }
      }

      navigate({
        to: route,
        search: search as AnySearch,
        replace: false,
      });
    },
    [navigate]
  );

  /**
   * Clears deep link parameters from the URL after they've been processed
   */
  const clearCallIntelDeepLinkParams = useCallback(
    (currentSearch: AnySearch, replace = true) => {
      navigate({
        search: omit(currentSearch, ['filters', 'tab', 'subViewType', 'subViewId', 'mailer', 'metric']) as AnySearch,
        replace,
      });
    },
    [navigate]
  );

  /**
   * Clears deep link parameters from the URL after they've been processed.
   * Legacy behavior: when mailer is present, redirects to main Call Intel page.
   */
  const clearDeepLinkParamsWithMailerRedirect = useCallback(
    (currentSearch: AnySearch) => {
      if (currentSearch?.mailer) {
        navigate({
          to: URLS.CALL_INTEL_MAIN_PAGE,
          search: omit(currentSearch, ['filters', 'tab', 'subViewType', 'subViewId', 'mailer', 'metric']) as AnySearch,
          replace: true,
        });
      } else {
        clearCallIntelDeepLinkParams(currentSearch);
      }
    },
    [navigate, clearCallIntelDeepLinkParams]
  );

  return {
    navigateToCallIntel,
    clearCallIntelDeepLinkParams,
    clearDeepLinkParamsWithMailerRedirect,
    hasDeepLinkParams,
    hasValidSubView,
  };
};
